import {
  Deck,
  FlyToInterpolator,
  Layer,
  MapView,
  PickingInfo,
  WebMercatorViewport,
} from '@deck.gl/core/typed';
import DeckGL from '@deck.gl/react/typed';
import { PopoverPosition } from '@mui/material/Popover';
import { Feature } from '@turf/helpers';
import StatusBarWrapper from 'components/StatusBarWrapper';
import { MapZoom } from 'components/maps/BaseMap//MapZoom';
import { LayersControl } from 'components/maps/BaseMap/LayersControl/LayersControl';
import { MapSearchBox } from 'components/maps/BaseMap/MapSearchBox/MapSearchBox';
import LayersToolbar from 'components/maps/LayersToolbar/LayersToolbar';
import {
  DeckTransitionProps,
  IKineticMapContext,
  KineticMapContext,
  MapContext,
} from 'components/maps/MapContext';
import { ContextMenuItem } from 'components/maps/MapMarker';
import { MapMarkerContext } from 'components/maps/MapMarkerContext';
import { useCtrlKey } from 'kineticUtils/hooks/useCtrlKey';
import { orderBy } from 'lodash';
import { MjolnirGestureEvent } from 'mjolnir.js';
import {
  ComponentType,
  PropsWithChildren,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import { Map as MapLibre, ViewState } from 'react-map-gl/maplibre';
import { useDeckGlTileLayers } from '../DeckGlTileLayers/useDeckGlTileLayers';
import { MapTooltipProvider, useMapTooltipContext } from '../contexts/MapTooltipProvider';
import { ViewStateProvider } from '../contexts/ViewState';
import { getScaledDuration } from '../utils';
import { MapContextMenu } from './MapContextMenu';
import { MapTooltip } from './MapTooltip';
import PickingIconLayer from './PickingIconLayer';
import { DOUBLE_CLICK_THRESHOLD, ENTITY_ZOOM, MAX_ZOOM } from './const';
import { useDebugMap } from './hooks/useDebugMap';
import { useWebGlContextCleanup } from './hooks/useWebGlCleanup';
import {
  CursorState,
  DeckGlMapContainerProps,
  GeoJsonLayerPickingInfo,
  KineticFeature,
  PickedObject,
  isPointMapObject,
} from './types';

import { MapTool } from 'components/maps/LayersToolbar/const';
import { useActiveMapTool } from 'components/maps/MapContext/hooks/useActiveMapTool';
import { useTranslation } from 'react-i18next';
import { MapLayerAttributions } from '../MapLayerBranding/MapLayerAttributions';
import { MapLayerLogo } from '../MapLayerBranding/MapLayerLogo';
import { GeoJsonLayerTooltip } from './GeoJsonLayerTooltip';
import { MainMapEntitiesDrawer } from './MainMapEntitiesDrawer';
import { useExtrudedGeoJsonLayer } from './hooks/useExtrudedGeoJsonLayer';
import { useFitBounds } from './hooks/useFitBounds';
import { useGeoJsonLayer } from './hooks/useGeoJsonLayer';
import { useMapMarkerLayer } from './hooks/useMapMarkerLayer';

const TOOLBAR_OFFSET = 43;

const DeckGlMapContainer = (props: PropsWithChildren<DeckGlMapContainerProps>) => {
  // NOTE: data contains both MapMarker and GeoJson data
  const { data, lastUpdate, mapMarkers } = useContext(MapMarkerContext);
  const { cursor, setAdditionalMapContextMenuItems } = useContext(MapContext);
  const mapRef = useRef<MapRef>();
  const deckRef = useRef<Deck>();
  const { setWebGlRef } = useWebGlContextCleanup();

  const [geoJson, setGeoJson] = useState<KineticFeature[]>([]);
  const [extrudedGeoJson, setExtrudedGeoJson] = useState<Feature[]>([]);

  const [canDrag, setCanDrag] = useState(true);
  const [ctrlDown, setCtrlDown] = useState(false);

  const [allContextItems, setAllContextItems] = useState<ContextMenuItem[]>([]);
  const [contextPosition, setContextPosition] = useState<PopoverPosition>();
  const [contextMenuCoordinates, setContextMenuCoordinates] = useState<number[]>();
  const lastClickTime = useRef(0);

  const { t } = useTranslation(['tunnels', 'corridors']);

  const {
    cluster,
    showMapMarkers = false,
    addToCache,
    bounds,
    children,
    viewState: viewStateProps,
    extraLayers,
    mapChildren,
    contextChildren,
    deckProps,
    mapContextMenu = [],
    mapEntityClickEvents,
    disableClusterSelection,
    previewLayers,
    toolbar,
    entityDrawer,
    searchBox,
    searchBoxPosition,
    layerButtonPosition,
    layerDrawerPosition,
    zoomButtonsPosition,
    disableExtraLayerClicks,
  } = props;

  const [viewState, setViewState] = useState<Partial<ViewState> & DeckTransitionProps>({
    longitude: 0,
    latitude: 0,
    zoom: 8,
    bearing: 0,
    pitch: 0,
  });

  const mercatorViewPort = useMemo(
    () =>
      new WebMercatorViewport({
        width: props.width,
        height: props.height,
      }),
    [props.height, props.width]
  );

  const fitBounds = useFitBounds({ setViewState, mercatorViewPort });

  useEffect(() => {
    if (bounds && !viewStateProps) {
      fitBounds(bounds as [[number, number], [number, number]]);
    }
    // only update on bounds or viewStateProps change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds, viewStateProps]);

  useEffect(() => {
    if (viewStateProps) {
      setViewState((prev) => {
        return { ...prev, ...viewStateProps };
      });
    }
  }, [viewStateProps]);

  useCtrlKey({ callback: setCtrlDown });

  const { style, layers, mapLayers, selectedBaseLayerConfig } = useDeckGlTileLayers(
    !!previewLayers,
    viewState.zoom
  );

  const lastUpdateTime = lastUpdate.getTime();

  // NOTE: Works in junction with the useEffect below to update the markers and geojson
  useEffect(() => {
    const geojson: KineticFeature[] = [];
    const extrudedGeojson: Feature[] = [];
    data.forEach((value) => {
      if (value.type && value.type === 'geojson') {
        geojson.push({
          type: 'Feature',
          geometry: value.geometry,
          properties: value,
        });
        if (value.extruded) {
          extrudedGeojson.push({ type: 'Feature', geometry: value.geometry, properties: value });
        }
      }
    });
    setGeoJson(geojson);
    setExtrudedGeoJson(extrudedGeojson);
    // watch on last update so we fire when data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdateTime]);

  const { hovered, onHover } = useMapTooltipContext();

  const handleGeoJsonLayerClick = useCallback(
    (info: PickingInfo, event: MjolnirGestureEvent) => {
      if (event.rightButton && info.object) {
        setContextPosition({
          top: (event.srcEvent as MouseEvent).clientY,
          left: (event.srcEvent as MouseEvent).clientX,
        });
        setContextMenuCoordinates(info.coordinate);
        setAllContextItems([...info.object.properties.contextMenuItems, ...mapContextMenu]);
      }
    },
    [mapContextMenu]
  );

  const { geoJsonLayer } = useGeoJsonLayer({
    geoJson,
    onClick: handleGeoJsonLayerClick,
  });

  const { extrudedGeoJsonLayer } = useExtrudedGeoJsonLayer({
    geoJson: extrudedGeoJson,
  });

  const { mapMarkerLayer } = useMapMarkerLayer({
    markers: data,
    cluster,
    showMapMarkers,
    addToCache,
    mapEntityClickEvents,
    disableClusterSelection,
    setViewState,
    setAllContextItems,
    setContextPosition,
    setContextMenuCoordinates,
  });

  const processedExtraLayers = (extraLayers ?? []).map((layer: Layer<any>) => {
    const shouldDisable = disableExtraLayerClicks && !(layer.props as any).alwaysEnableClick;
    return shouldDisable && layer.props.onClick ? layer.clone({ onClick: null }) : layer;
  });

  const deckGlLayers = useMemo(() => {
    if (previewLayers) {
      return previewLayers;
    }
    const allLayers = [
      ...layers,
      ...processedExtraLayers,
      mapMarkerLayer,
      geoJsonLayer,
      extrudedGeoJsonLayer,
    ];
    return orderBy(allLayers, [(layer: PickingIconLayer) => layer.props?.order ?? 0]);
  }, [
    previewLayers,
    layers,
    processedExtraLayers,
    mapMarkerLayer,
    geoJsonLayer,
    extrudedGeoJsonLayer,
  ]);

  const onViewStateChange = useCallback(({ viewState }) => {
    setViewState(viewState);
  }, []);

  const getCursor = useCallback(
    (event: CursorState) => {
      return cursor ? cursor : event.isDragging ? 'grabbing' : 'grab';
    },
    [cursor]
  );

  const mapContextValue = useMemo(
    () =>
      ({
        deckRef,
        fitBounds,
        setViewState,
        mapRef,
        setZoom: (zoom) => setViewState((prev) => ({ ...prev, zoom })),
        setDragEnabled: setCanDrag,
        selectedBaseLayerConfig,
      } as IKineticMapContext),
    [fitBounds, selectedBaseLayerConfig]
  );

  const mapView = useMemo(
    () =>
      new MapView({
        repeat: true,
        controller: {
          dragPan: canDrag,
          dragMode: 'pan',
          dragRotate: ctrlDown,
          scrollZoom: {
            smooth: true,
          },
          doubleClickZoom: false,
        },
      }),
    [canDrag, ctrlDown]
  );

  const onContextMenu = useCallback((evt) => {
    evt.preventDefault();
  }, []);

  const { activeMapTool } = useActiveMapTool();

  const layerSpecificContextMenuItems = useMemo(() => {
    return [t('tunnels:tunnelDetails'), t('corridors:corridorDetails')];
  }, [t]);

  const onClick = useCallback(
    (info: PickedObject<unknown>, event: MjolnirGestureEvent) => {
      const mouseEvent = event.srcEvent as MouseEvent;
      const isRightClick = event.rightButton;
      const currentTime = new Date().getTime();
      const isGeoJsonMapLayer = (info as PickingInfo).object?.geometry?.type === 'Polygon';
      const allowRightClick = (info as PickingInfo).object?.properties?.allowRightClick;

      if (!allowRightClick) {
        // NOTE: this is a hack, we should not be appending
        // to the main map context menu without clean-up
        // we might want to consider a new approach
        setAdditionalMapContextMenuItems((prev) => {
          // Remove any existing layer-specific context menu items when clicking the base layers
          return prev.filter((item) => !layerSpecificContextMenuItems.includes(item.text));
        });
      }

      if (isRightClick) {
        if (!info.picked || isGeoJsonMapLayer || allowRightClick) {
          setContextPosition({
            top: mouseEvent.clientY,
            left: mouseEvent.clientX,
          });
          setContextMenuCoordinates(info.coordinate);
          setAllContextItems([
            ...((info as PickingInfo).object?.properties?.contextMenuItems || []),
            ...mapContextMenu,
          ]);
        }
      } else if (mouseEvent.shiftKey) {
        setViewState((prev) => ({
          ...prev,
          latitude: info.coordinate?.[1],
          longitude: info.coordinate?.[0],
          zoom: Math.max(prev.zoom!, ENTITY_ZOOM),
          transitionDuration: getScaledDuration(
            prev.latitude!,
            prev.longitude!,
            info.coordinate![1],
            info.coordinate![0],
            prev.zoom!,
            Math.max(prev.zoom!, ENTITY_ZOOM)
          ),
          transitionInterpolator: new FlyToInterpolator(),
        }));
      } else if (currentTime - lastClickTime.current < DOUBLE_CLICK_THRESHOLD) {
        setViewState((prev) => {
          if (isPointMapObject(info)) {
            //Double click on icon
            return {
              ...prev,
              latitude: info.object!.geometry.coordinates[1],
              longitude: info.object!.geometry.coordinates[0],
              zoom: Math.max(prev.zoom!, ENTITY_ZOOM),
              transitionDuration: getScaledDuration(
                prev.latitude!,
                prev.longitude!,
                info.object!.geometry.coordinates[1],
                info.object!.geometry.coordinates[0],
                prev.zoom!,
                Math.max(prev.zoom!, ENTITY_ZOOM)
              ),
              transitionInterpolator: new FlyToInterpolator(),
            };
          } else {
            // double click, but area tool is up, don't zoom
            if (activeMapTool === MapTool.AREA) {
              return prev;
            }
            //Double click elsewhere on the map
            return {
              ...prev,
              latitude: info.coordinate![1],
              longitude: info.coordinate![0],
              zoom: Math.min(prev.zoom! + 1, MAX_ZOOM),
              transitionDuration: 500,
              transitionInterpolator: new FlyToInterpolator(),
            };
          }
        });
      }

      lastClickTime.current = currentTime;
    },
    [activeMapTool, layerSpecificContextMenuItems, mapContextMenu, setAdditionalMapContextMenuItems]
  );

  const { DEBUG_MAP_ID } = useDebugMap({ viewState });

  const isGeoJsonLayerTooltip = useMemo(
    () => !!(hovered as GeoJsonLayerPickingInfo)?.layer?.props?.getLayerTooltip,
    [hovered]
  );

  return (
    <KineticMapContext.Provider value={mapContextValue}>
      <ViewStateProvider value={viewState}>
        <div
          id={DEBUG_MAP_ID}
          onContextMenu={onContextMenu}
          style={{ display: 'flex', height: '100%', width: '100%' }}
        >
          <DeckGL
            onWebGLInitialized={setWebGlRef}
            ref={deckRef}
            views={mapView}
            initialViewState={viewState}
            viewState={viewState}
            onViewStateChange={onViewStateChange}
            layers={deckGlLayers}
            getCursor={getCursor}
            onClick={onClick}
            onHover={onHover}
            onLoad={() =>
              // trigger a rerender on load to render children
              setViewState((prev) => ({ ...prev }))
            }
            style={{ zIndex: 'unset' }}
            {...((deckProps as any) ?? {})}
          >
            <MapLibre
              onLoad={() => setViewState((prev) => ({ ...prev }))}
              reuseMaps
              dragRotate={false}
              ref={mapRef as any}
              mapStyle={style}
            >
              {mapLayers}
              {mapChildren}
            </MapLibre>
            {hovered?.object && isGeoJsonLayerTooltip && (
              <GeoJsonLayerTooltip
                info={hovered as GeoJsonLayerPickingInfo}
                width={props.width}
                height={props.height}
              />
            )}
            {hovered?.object && !isGeoJsonLayerTooltip && (
              <MapTooltip info={hovered as any} width={props.width} height={props.height} />
            )}
            <Suspense key='childrenSuspense' fallback={<></>}>
              {!!mapRef.current && !!deckRef.current && children}
            </Suspense>
          </DeckGL>

          <Suspense key='contextChildrenSuspense' fallback={<></>}>
            {!!mapRef.current && !!deckRef.current && (
              <>
                {contextChildren}
                {toolbar ? (
                  <>
                    <LayersToolbar
                      key='entity-drawer-toolbar'
                      showEntityDrawerButton={!!entityDrawer}
                    />
                    <MainMapEntitiesDrawer entityDrawer={entityDrawer} />
                  </>
                ) : (
                  <LayersControl
                    drawerPosition={layerDrawerPosition ?? 'left'}
                    buttonPosition={layerButtonPosition ?? 'top'}
                  />
                )}
                {toolbar && <StatusBarWrapper />}
                {searchBox && (
                  <MapSearchBox
                    searchBoxPosition={searchBoxPosition}
                    topOffset={!!toolbar ? TOOLBAR_OFFSET : 0}
                  />
                )}
                <MapZoom
                  zoomButtonsPosition={zoomButtonsPosition}
                  topOffset={!!toolbar ? TOOLBAR_OFFSET : 0}
                />
                <MapLayerAttributions />
                <MapLayerLogo />
              </>
            )}
          </Suspense>
        </div>
        {!!allContextItems.length && (
          <MapContextMenu
            handleClose={() => {
              setContextPosition(undefined);
              setAllContextItems([]);
            }}
            items={allContextItems}
            position={contextPosition}
            coordinates={contextMenuCoordinates}
          />
        )}
      </ViewStateProvider>
    </KineticMapContext.Provider>
  );
};

// TODO: move ViewStateProvider and KineticMapContext in here?
const withProviders = (Component: ComponentType) => {
  return (props: PropsWithChildren<DeckGlMapContainerProps>) => {
    return (
      <MapTooltipProvider>
        <Component {...props} />
      </MapTooltipProvider>
    );
  };
};

export default withProviders(DeckGlMapContainer as any);
